// A connector selection carries only a componentId. Everything else consumers
// read — ids, alias, images, neighbour ids — is a projection of the block diagram
// graph, resolved here.
//
// The same predicates decide what the diagram draws (see prepareNodes), so a
// resolved selection can never point at an element that isn't on screen.

// Neighbour component ids of a connector's ports.
export const getConnectorNeighbors = (portNode) =>
  portNode
    .parent()
    .children()
    .filter(
      (element) => element.data('componentId') === portNode.data('componentId'),
    )
    .openNeighborhood()
    .filter((element) => element.isNode())
    .map((node) => node.data('componentId'))

export const createRenderFilters = ({
  hideDisconnectedParents = true,
  hideDisconnectedPorts = true,
  hideSelfLoops = true,
} = {}) => {
  // Ports that connect to nothing, or only back to their own device, are not
  // drawn.
  const isRenderedPort = (child) => {
    if (hideDisconnectedPorts && child.connectedEdges().length === 0)
      return false

    if (hideSelfLoops) {
      const selfLoopExists = child
        .connectedEdges()
        .some(
          (edge) => edge.source().parent().id() === edge.target().parent().id(),
        )
      if (selfLoopExists) return false
    }

    return true
  }

  // Devices whose ports connect to no other device are not drawn.
  const isRenderedParent = (parent) =>
    !hideDisconnectedParents || parent.children().connectedEdges().length > 0

  return { isRenderedPort, isRenderedParent }
}

// Returns null when the connector has no port in the rendered diagram, so the
// caller can say so rather than showing a selection with nothing highlighted.
export const resolveConnector = ({ graph, componentId, filters }) => {
  const { isRenderedPort, isRenderedParent } = filters

  const portNode = graph
    .nodes()
    .filter(
      (node) =>
        !node.isParent() &&
        node.data('componentId') === componentId &&
        isRenderedPort(node) &&
        isRenderedParent(node.parent()),
    )
    .first()

  if (portNode.length === 0) return null

  const {
    imageName,
    description,
    color,
    alias,
    locationImageName,
    partNumber,
  } = portNode.data()

  return {
    // Every port of this connector, so the graph highlights all of them.
    ids: portNode
      .parent()
      .children()
      .filter((element) => element.data('componentId') === componentId)
      .map((port) => port.id()),
    imageName,
    description,
    color,
    alias,
    locationImageName,
    partNumber,
    neighborIds: getConnectorNeighbors(portNode),
  }
}
